/**
 * 送付状の枠（#223、docs/89 §5）。帳票の紙の画面（見積書・請求書・領収書・見積依頼書）・見積依頼の「送付」の確認・単体送付状で同じ部品。
 *   useCoverLetter(target)：初めの値（POST /cover-letters/draft）→ この出力だけの中身（テンプレートは変わらない）→ 見本（POST /cover-letters/preview、打つたびに 0.8 秒後）
 *   CoverLetterPanel：送付状（スイッチ）・窓付封筒（スイッチ。**送付状がオンのときだけ出す**＝★8、オフに戻すと窓付封筒もオフ）・テンプレート（2つ以上のとき）・
 *     送付先・送信先 FAX（FAX の型）・日付・件名・題・挨拶・本文・備考・記（自動の行＋手の行）・本状含め n枚。
 *     窓付封筒で宛名を 9pt まで縮めても窓に入らなければ、窓付封筒の行の下に注意の1行（見本の答えのヘッダー x-gb-cover-address＝long）
 *   印刷・PDF の保存は呼ぶ側（deliver＝POST /deliveries で送付状＋同封書類の PDF 1本を作って固める）
 *   金額は出さない（★4）。PC だけ（docs/89 §15 の14＝スマホの紙の画面には出さない）
 * 2回目（束ね、docs/89 §6）：
 *   ・同封書類が変わっても（書類を追加・上へ／下へ・削除）送付先が同じなら枠で直した中身はそのまま（送付先が変わったときだけ初めの値に戻す）
 *   ・初めの値：テンプレートは{customer}ごとの既定 → 会社の既定（API が決める）、窓付封筒は{customer}ごとの既定（窓付封筒がオンなら送付状もオンで始める）
 *   ・記：bundle を渡すと同封書類の行に「上へ」「下へ」「削除」と書類名の欄（受け取った書類・その場の PDF）。onAddDocs で「書類追加」
 *   ・束ねの上限（書類 20・本状含め 50 ページ・20MB）を超えたら記の下に朱の1行（印刷・保存は API も止める）
 * 3回目の前の直し（2026-10-21 射場さんの決め、docs/89 §17）：
 *   ・未登録の送付先（★13。target.unregistered＝初めの値の送付先の id が null）：送付先の欄に 会社名・部署・担当者・郵便番号・住所（FAX は送信先 FAX）と
 *     「{customer}に登録」（既定オフ）。担当者の選択と「{customer}に登録」（FAX 番号）は出さない
 *   ・FAX の欄の下は「FAX 番号が未登録です」の1行（説明は名前の横の「？」＝★19）
 *   ・見本の透かし（watermark。見積依頼の送付の画面＝「確認用」・PDF で確認＝「下書き」。★16）。印刷・保存の PDF には付かない
 *   ・記の行の fixed＝削除できない同封書類（見積依頼の送付の画面の見積依頼書）
 * {order}の約款（#233 2c、docs/93 §14.2）：terms を渡すとスイッチ「約款」（約款を本体と分けた別紙で同封するか。送付状のオン／オフに関わらず出す）。
 *   オフのときの注意の1行は出さない（2026-10-27、docs/93 §16.4 ヌ＝設定の注意文・発行前の確認の参考と同じ扱い）
 */
import {
  COVER_ADDRESS_HEADER,
  COVER_GREETING_LABEL,
  COVER_PDF_PAGES_HEADER,
  bundleLimitError,
  COVER_LETTER_LIMITS,
  COVER_LETTER_MSG as M,
  COVER_TOTAL_PAGES_HEADER,
  ORDER_MSG,
  coverLineText,
  type CoverDraft,
  type CoverGreeting,
  type CoverLetterContentInput,
  type CoverLetterTemplate,
  type CoverMode,
  type CoverRecipientInput,
  type DeliveryResult,
  type DeliveryTargetInput,
} from "@genbaryoku/contract";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { api, fetchFile, messageOf } from "../api.js";
import { renderPdf, type PageImg } from "../lib/pdfPages.js";
import { t } from "../labels.js";
import { DateInput } from "./DateInput.jsx";
import { NumInput } from "./NumInput.jsx";
import { Segmented } from "./Segmented.jsx";
import { ErrorText, Field, Help, Switch } from "./ui.jsx";

type Manual = { label: string; copies: number; pages: number | null };
export type CoverForm = {
  templateId: string;
  mode: CoverMode;
  date: string;
  subject: string;
  title: string;
  greeting: CoverGreeting;
  body: string;
  remarks: string;
  toFax: string;
  contactId: string | null;
  manualLines: Manual[];
  /** その場で打った FAX 番号を{customer}に登録する（既定オフ。docs/89 §15 の6） */
  saveFax: boolean;
  /** 未登録の送付先（★13）：会社名・部署・担当者・郵便番号・住所（FAX は toFax） */
  recipient: CoverRecipientInput;
  /** 未登録の送付先を{customer}に登録してから送付する（既定オフ。★13） */
  registerPartner: boolean;
};
const EMPTY_RECIPIENT: CoverRecipientInput = { name: "", department: "", contact: "", postalCode: "", address: "" };

const titleOf = (tpl: CoverLetterTemplate | undefined, mode: CoverMode) => (tpl ? (mode === "window" ? tpl.titleWindow : tpl.titleFax) : "");
const greetingOf = (tpl: CoverLetterTemplate | undefined, mode: CoverMode): CoverGreeting => (tpl ? (mode === "window" ? tpl.greetingWindow : tpl.greetingFax) : "brief");

function formOf(d: CoverDraft, templateId: string, mode: CoverMode): CoverForm {
  const tpl = d.templates.find((x) => x.id === templateId) ?? d.templates[0];
  return {
    templateId: tpl?.id ?? templateId,
    mode,
    date: d.date,
    subject: d.subject,
    title: titleOf(tpl, mode),
    greeting: greetingOf(tpl, mode),
    body: tpl?.body ?? "",
    remarks: tpl?.remarks ?? "",
    toFax: d.toFax,
    contactId: d.contactId,
    manualLines: [],
    saveFax: false,
    recipient: EMPTY_RECIPIENT,
    registerPartner: false,
  };
}

/** 送る形（POST /cover-letters/preview・/deliveries の cover）。unregistered＝未登録の送付先（★13）なら recipient も送る */
export const contentOf = (f: CoverForm, unregistered = false): CoverLetterContentInput => ({
  templateId: f.templateId,
  mode: f.mode,
  date: f.date,
  subject: f.subject,
  title: f.title,
  greeting: f.greeting,
  body: f.body,
  remarks: f.remarks,
  toFax: f.toFax,
  contactId: f.contactId,
  manualLines: f.manualLines.filter((l) => l.label.trim()).map((l) => ({ label: l.label, copies: l.copies, pages: l.pages })),
  ...(unregistered ? { recipient: f.recipient } : {}),
});

/**
 * 送付状の中身と見本。target が null のあいだは何もしない（下書き・スマホ）。
 *   switchable：送付状のスイッチがある（帳票の紙の画面・見積依頼）。無ければいつもオン（単体送付状）
 *   defaultOn：スイッチの初めの値（省けば会社の「印刷で送付状」）。defaultWindow：窓付封筒の初めの値（見積依頼の送信設定）
 */
export function useCoverLetter(
  target: DeliveryTargetInput | null,
  opts: {
    switchable?: boolean;
    defaultOn?: boolean;
    /** 窓付封筒の初めの値（見積依頼の送信設定）。省けば{customer}ごとの既定 */
    defaultWindow?: boolean;
    preview?: boolean;
    /** 送る前の見積依頼書（「送付」の確認の窓） */
    draftRfq?: boolean;
    /** 初めの中身（帳票の紙の画面から束ねの画面へ移ったとき、枠で直した中身を引き継ぐ） */
    initial?: { form: CoverForm; on: boolean } | null;
    /** 見本の透かし（★16）：confirm＝「確認用」（見積依頼の送付の画面）・draft＝「下書き」（PDF で確認）。印刷・保存には付かない */
    watermark?: "draft" | "confirm" | undefined;
    /**
     * メールの送付状（#225、docs/91 §4）：型はいつも窓付封筒の紙（題「書類送付のご案内」・宛名は左上）で、見本も折り目の印と FAX の通し番号なし。
     * 送付状のスイッチの初めは オフ（★2 のメールでは既定オフ）
     */
    mail?: boolean;
  } = {},
) {
  const { switchable = true, preview = true, draftRfq = false } = opts;
  const unregistered = !!target?.unregistered;
  const key = target ? JSON.stringify(target) : null;
  // 同封書類が変わっても前の答えを出したまま（枠を空にしない）。送付先が同じなら中身は下の useEffect で残す
  const draft = useQuery({ queryKey: ["cover-draft", key, draftRfq], queryFn: () => api<CoverDraft>("POST", "/cover-letters/draft", { target, draftRfq }), enabled: !!target, retry: false, staleTime: 60_000, placeholderData: keepPreviousData });
  const [on, setOnState] = useState<boolean | null>(null);
  const [form, setForm] = useState<CoverForm | null>(null);
  /** 題・挨拶を手で変えたか（変えていなければ、型・テンプレートを変えたときにテンプレートの値に入れ替える） */
  const touched = useRef({ title: false, greeting: false });
  /** 中身を作った送付先（取引先と支店）。同じ送付先のあいだは同封書類が変わっても中身を作り直さない */
  const loadedFor = useRef<string | null>(null);
  const initialUsed = useRef(false);
  useEffect(() => {
    const d = draft.data;
    if (!d || draft.isPlaceholderData) return;
    const rk = `${d.partner.id}|${d.branch?.id ?? ""}`;
    if (loadedFor.current === rk) return;
    loadedFor.current = rk;
    if (opts.initial && !initialUsed.current) {
      initialUsed.current = true;
      touched.current = { title: true, greeting: true };
      setForm(opts.initial.form);
      setOnState(switchable ? opts.initial.on : true);
      return;
    }
    touched.current = { title: false, greeting: false };
    const win = opts.mail || (opts.defaultWindow ?? d.defaultWindow);
    setForm(formOf(d, d.templateId, win ? "window" : "fax"));
    // {customer}ごとの既定で窓付封筒なら送付状もオン（窓付封筒は送付状があるときだけ＝★8）
    setOnState(switchable ? (opts.mail ? false : (opts.defaultOn ?? (d.printDefault || d.defaultWindow))) : true);
  }, [draft.data, draft.isPlaceholderData]); // eslint-disable-line react-hooks/exhaustive-deps
  const tplOf = (id: string) => draft.data?.templates.find((x) => x.id === id);
  /** 送付状のスイッチ：オフに戻すと窓付封筒もオフ（★8） */
  const setOn = (v: boolean) => {
    setOnState(v);
    if (!v) setForm((f) => (f && f.mode === "window" ? switchMode(f, "fax") : f));
  };
  const switchMode = (f: CoverForm, mode: CoverMode): CoverForm => {
    const tpl = tplOf(f.templateId);
    return { ...f, mode, title: touched.current.title ? f.title : titleOf(tpl, mode), greeting: touched.current.greeting ? f.greeting : greetingOf(tpl, mode) };
  };
  const setMode = (mode: CoverMode) => setForm((f) => (f ? switchMode(f, mode) : f));
  const setTemplate = (id: string) =>
    setForm((f) => {
      if (!f) return f;
      const tpl = tplOf(id);
      touched.current = { title: false, greeting: false };
      return { ...f, templateId: id, title: titleOf(tpl, f.mode), greeting: greetingOf(tpl, f.mode), body: tpl?.body ?? f.body, remarks: tpl?.remarks ?? f.remarks };
    });
  const update = (p: Partial<CoverForm>) => {
    if (p.title !== undefined) touched.current.title = true;
    if (p.greeting !== undefined) touched.current.greeting = true;
    setForm((f) => (f ? { ...f, ...p } : f));
  };

  // 見本：送付状のページだけの PDF（0.8 秒待ってから。前の見本は来るまで残す）
  const [pages, setPages] = useState<PageImg[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  /** 出す PDF のページ数（送付状＋同封書類）＝束ねの上限を見る */
  const [pdfPages, setPdfPages] = useState<number | null>(null);
  const [addressLong, setAddressLong] = useState(false);
  const [previewErr, setPreviewErr] = useState<string | undefined>();
  const [previewing, setPreviewing] = useState(false);
  const body = useMemo(() => (form ? JSON.stringify({ target, cover: contentOf(form, unregistered), draftRfq, ...(opts.watermark ? { watermark: opts.watermark } : {}), ...(opts.mail ? { mail: true } : {}) }) : null), [form, key, draftRfq, opts.watermark, opts.mail]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!preview || !on || !body) return;
    let alive = true;
    setPreviewing(true);
    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          let n: number | null = null;
          let pp: number | null = null;
          let long = false;
          const blob = await fetchFile("/api/v1/cover-letters/preview", {
            method: "POST",
            body: JSON.parse(body),
            onHeaders: (h) => {
              n = Number(h.get(COVER_TOTAL_PAGES_HEADER)) || null;
              pp = Number(h.get(COVER_PDF_PAGES_HEADER)) || null;
              long = h.get(COVER_ADDRESS_HEADER) === "long";
            },
          });
          if (!alive) return;
          const got: PageImg[] = [];
          await renderPdf(await blob.arrayBuffer(), (p, i) => {
            got[i - 1] = p;
          });
          if (!alive) return;
          setPages(got);
          setTotal(n);
          setPdfPages(pp);
          setAddressLong(long);
          setPreviewErr(undefined);
        } catch (e) {
          if (alive) setPreviewErr(messageOf(e));
        } finally {
          if (alive) setPreviewing(false);
        }
      })();
    }, 800);
    return () => {
      alive = false;
      window.clearTimeout(timer);
    };
  }, [body, on, preview]);

  /**
   * 印刷・PDF の保存：送付状＋同封書類の PDF 1本を作って固める → PDF と保存名。
   * over＝送り先を変えて送る（未登録の送付先を{customer}に登録したあと＝登録した{customer}あてに。★13）
   */
  const deliver = async (method: "print" | "download", over?: { target: DeliveryTargetInput; cover: CoverLetterContentInput }): Promise<{ result: DeliveryResult; blob: Blob }> => {
    if (!form || !target) throw new Error("送付状を読み込んでいます");
    const result = await api<DeliveryResult>("POST", "/deliveries", over ? { method, ...over } : { method, target, cover: contentOf(form, unregistered), saveFax: !unregistered && form.saveFax && !draft.data?.toFax && !!form.toFax.trim() });
    const blob = await fetchFile(result.pdfUrl);
    return { result, blob };
  };
  // 束ねの上限（書類 20・本状含め 50 ページ・20MB）。見本が来る前は送付状を1枚と見る
  const d = draft.data;
  const bundleError = d && on ? bundleLimitError({ items: d.items.length, pages: pdfPages ?? d.docPages + 1, bytes: d.bytes }) : null;
  return { draft, on: !!on, setOn, form, update, setMode, setTemplate, pages: on ? pages : [], total: on ? total : null, addressLong: !!on && form?.mode === "window" && addressLong, previewErr, previewing, deliver, ready: !!form && on !== null, bundleError, unregistered };
}
export type CoverLetter = ReturnType<typeof useCoverLetter>;

/** {order}の約款の別紙のスイッチ（#233 2c）。rows＝別紙にできる{order}ごと（id＝{order}の id） */
export type CoverTermsControls = { rows: ReadonlyArray<{ id: string; label: string; on: boolean }>; onChange: (id: string, on: boolean) => void };

/** 枠（PC・幅 360px）。collapsible＝見出しの右の ⌃（開閉はこの端末に覚える） */
/** 束ねの画面の記の操作（2回目）：i は同封書類の何番目か */
export type BundleControls = {
  onMove: (i: number, dir: -1 | 1) => void;
  onRemove: (i: number) => void;
  /** 書類名（受け取った書類・その場の PDF）。key＝同封書類の行の鍵 */
  labels: Record<string, string>;
  onLabel: (key: string, v: string) => void;
  /** 削除できない同封書類の鍵（見積依頼の送付の画面の見積依頼書。上へ・下へはできる） */
  fixed?: ReadonlySet<string>;
};

export function CoverLetterPanel({
  c,
  switchable = true,
  collapsible = true,
  showWindow = true,
  bundle,
  onAddDocs,
  addDocsLabel = M.addDocs,
  terms,
  testId = "cover-panel",
}: {
  c: CoverLetter;
  switchable?: boolean;
  collapsible?: boolean;
  /** 窓付封筒のスイッチを枠の中に出す（見積依頼の「送付」の窓は送信設定の帯にあるので出さない） */
  showWindow?: boolean;
  /** 束ねの画面（2回目）：記の同封書類の行に 上へ・下へ・削除・書類名の欄 */
  bundle?: BundleControls | undefined;
  /** 「書類追加」（束ねの画面）・「同じ送付先の書類を追加」（帳票の紙の画面） */
  onAddDocs?: (() => void) | undefined;
  addDocsLabel?: string;
  /** {order}の約款の別紙（#233 2c） */
  terms?: CoverTermsControls | undefined;
  testId?: string;
}) {
  const [open, setOpenState] = useState(() => {
    try {
      return window.localStorage.getItem("gb.cover.panel") !== "closed";
    } catch {
      return true;
    }
  });
  const setOpen = (v: boolean) => {
    setOpenState(v);
    try {
      window.localStorage.setItem("gb.cover.panel", v ? "open" : "closed");
    } catch {
      /* 覚えられなくても動く */
    }
  };
  const ids = useId();
  const d = c.draft.data;
  const f = c.form;
  if (c.draft.isError) {
    return (
      <aside className="clpanel" aria-label={M.panelTitle} data-testid={testId}>
        <ErrorText>{messageOf(c.draft.error)}</ErrorText>
      </aside>
    );
  }
  if (!d || !f) {
    return (
      <aside className="clpanel is-busy" aria-label={M.panelTitle} data-testid={testId} aria-busy="true">
        <div className="clhead">
          <b>{M.panelTitle}</b>
        </div>
        <p className="hint">{M.making}</p>
      </aside>
    );
  }
  const fax = f.mode === "fax";
  const auto = d.autoLines;
  const lineNo = (i: number) => `${i + 1}.`;
  // 束ねの画面：同封書類の行（並びの順。見積依頼書の写真台帳は下の段の行）
  const bundleRows = bundle
    ? (() => {
        let n = 0;
        return d.items.map((it, i) => ({ it, i, no: ++n, extra: it.extraLines.map((l) => ({ l, no: ++n })) }));
      })()
    : null;
  const manualStart = bundleRows ? bundleRows.reduce((a, r) => a + 1 + r.extra.length, 0) : auto.length;
  const manualMax = f.manualLines.length >= COVER_LETTER_LIMITS.manualLines;
  const setLine = (i: number, p: Partial<Manual>) => c.update({ manualLines: f.manualLines.map((l, j) => (j === i ? { ...l, ...p } : l)) });
  return (
    <aside className={"clpanel" + (open ? " is-open" : "")} aria-label={M.panelTitle} data-testid={testId}>
      <div className="clhead">
        {switchable ? (
          <Switch checked={c.on} onChange={c.setOn} label={M.coverSwitch} testId="cover-switch">
            <b>{M.coverSwitch}</b>
          </Switch>
        ) : (
          <b>{M.panelTitle}</b>
        )}
        <span className="sp" />
        {/* 本状含めの枚数は FAX の型だけ（窓付封筒の紙には出ない） */}
        {c.on && c.total && f.mode === "fax" ? (
          <span className="chip" data-testid="cover-total">
            {M.totalPages.replace("{n}", String(c.total))}
          </span>
        ) : null}
        {collapsible ? (
          <button type="button" className="btn text clfold" aria-expanded={open} aria-label={open ? M.panelClose : M.panelOpen} onClick={() => setOpen(!open)} data-testid="cover-fold">
            <span className={"vqd-chev" + (open ? " is-open" : "")} aria-hidden="true" />
          </button>
        ) : null}
      </div>
      {/* ★8：窓付封筒は送付状がオンのときだけ（オフなら行ごと出さない） */}
      {c.on && showWindow ? (
        <div className="clrow clwin">
          <Switch checked={!fax} onChange={(v) => c.setMode(v ? "window" : "fax")} label={M.windowSwitch} testId="cover-window">
            <span>{M.windowSwitch}</span>
          </Switch>
          <Help label={M.windowSwitch} text={M.windowHelp} testId="help-cover-window" />
        </div>
      ) : null}
      {terms ? <TermsRows x={terms} /> : null}
      {c.addressLong ? (
        <p className="err" role="alert" data-testid="cover-address-long">
          {`⚠ ${M.addressTooLong}`}
        </p>
      ) : null}
      {c.on && open ? (
        <div className="clbody">
          {d.templates.length > 1 ? (
            <Field id={`${ids}t`} label={M.template}>
              <select id={`${ids}t`} className="inp" value={f.templateId} onChange={(e) => c.setTemplate(e.target.value)} data-testid="cover-template">
                {d.templates.map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name}
                    {x.isDefault ? `（${M.defaultMark}）` : ""}
                  </option>
                ))}
              </select>
            </Field>
          ) : null}
          {c.unregistered ? (
            <UnregisteredFields ids={ids} f={f} update={c.update} />
          ) : (
            <div className="clto" data-testid="cover-to">
              <span className="cllbl">{M.to}</span>
              {/* docs/94 B-9：支店が無く宛名の欄（下の選ぶ欄＝「○○ 御中」）があるときは同じ名前を2回出さない＝宛名の欄だけ */}
              {!d.branch && d.contacts.length ? null : (
                <>
                  <b>{d.partner.name}</b>
                  {d.branch ? <span>{d.branch.name}</span> : null}
                </>
              )}
            </div>
          )}
          {!c.unregistered && d.contacts.length ? (
            <select className="inp" aria-label={M.to} value={f.contactId ?? ""} onChange={(e) => c.update({ contactId: e.target.value || null })} data-testid="cover-contact">
              <option value="">{`${d.branch ? d.branch.name : d.partner.name} 御中`}</option>
              {d.contacts.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.label}
                </option>
              ))}
            </select>
          ) : null}
          {fax ? (
            <Field id={`${ids}f`} label={M.toFax} help={<Help label={M.toFax} text={t(M.toFaxHelp)} testId="help-cover-fax" />}>
              <input id={`${ids}f`} className="inp" inputMode="tel" autoComplete="off" placeholder=" " value={f.toFax} maxLength={COVER_LETTER_LIMITS.fax} onChange={(e) => c.update({ toFax: e.target.value })} data-testid="cover-fax" />
            </Field>
          ) : null}
          {/* ★19：1行だけ（説明は名前の横の「？」） */}
          {fax && !f.toFax.trim() ? <p className="hint" data-testid="cover-fax-missing">{M.toFaxMissing}</p> : null}
          {c.unregistered ? <RegisterPartnerCheck f={f} update={c.update} /> : null}
          {fax && !c.unregistered && !d.toFax && f.toFax.trim() ? (
            <label className="clcheck">
              <input type="checkbox" checked={f.saveFax} onChange={(e) => c.update({ saveFax: e.target.checked })} data-testid="cover-save-fax" />
              <span>{t(M.saveFaxToPartner)}</span>
            </label>
          ) : null}
          <div className="clrow2">
            <DateInput id={`${ids}d`} label={M.date} value={f.date} clearable={false} onChange={(v) => v && c.update({ date: v })} testId="cover-date" />
          </div>
          <Field id={`${ids}s`} label={M.subject}>
            <input id={`${ids}s`} className="inp" placeholder=" " value={f.subject} maxLength={COVER_LETTER_LIMITS.subject} onChange={(e) => c.update({ subject: e.target.value })} data-testid="cover-subject" />
          </Field>
          <Field id={`${ids}h`} label={M.title}>
            <input id={`${ids}h`} className="inp" placeholder=" " value={f.title} maxLength={COVER_LETTER_LIMITS.title} onChange={(e) => c.update({ title: e.target.value })} data-testid="cover-title" />
          </Field>
          <div className="clrow">
            <span className="cllbl" id={`${ids}g`}>
              {M.greeting}
            </span>
            <Segmented
              options={(["none", "brief", "formal"] as const).map((g) => ({ value: g, label: COVER_GREETING_LABEL[g], testId: `cover-greeting-${g}` }))}
              value={f.greeting}
              onChange={(g) => c.update({ greeting: g })}
              labelledBy={`${ids}g`}
              testId="cover-greeting"
            />
            <Help label={M.greeting} text={M.greetingHelp} testId="help-cover-greeting" />
          </div>
          <Field id={`${ids}b`} label={M.body} help={<Help label={M.body} text={M.fieldsHelp} testId="help-cover-fields" />}>
            <textarea id={`${ids}b`} className="inp" rows={4} placeholder=" " value={f.body} maxLength={COVER_LETTER_LIMITS.body} onChange={(e) => c.update({ body: e.target.value })} data-testid="cover-body" />
          </Field>
          <Field id={`${ids}r`} label={M.remarks}>
            <textarea id={`${ids}r`} className="inp" rows={2} placeholder=" " value={f.remarks} maxLength={COVER_LETTER_LIMITS.remarks} onChange={(e) => c.update({ remarks: e.target.value })} data-testid="cover-remarks" />
          </Field>
          <div className="cllines" role="group" aria-label={M.lines} data-testid="cover-lines">
            <span className="cllbl">{M.lines}</span>
            <ol>
              {bundleRows
                ? bundleRows.flatMap(({ it, i, no, extra }) => [
                    <li key={it.key} className="is-item" data-testid="cover-bundle-item" data-key={it.key}>
                      <span className="no">{`${no}.`}</span>
                      {it.editable ? (
                        <input
                          className="inp sm"
                          aria-label={`${no}. ${M.itemLabel}`}
                          value={bundle!.labels[it.key] ?? it.label}
                          maxLength={COVER_LETTER_LIMITS.lineLabel}
                          onChange={(e) => bundle!.onLabel(it.key, e.target.value)}
                          data-testid="cover-bundle-label"
                        />
                      ) : (
                        <span className="nm">{coverLineText(it)}</span>
                      )}
                      <span className="pg">{M.pagesShort.replace("{n}", String(it.pages))}</span>
                      <span className="ops">
                        <button type="button" className="btn text sm" disabled={i === 0} aria-label={`${coverLineText(it)}を${M.moveUp}`} onClick={() => bundle!.onMove(i, -1)} data-testid="cover-bundle-up">
                          {M.moveUp}
                        </button>
                        <button type="button" className="btn text sm" disabled={i === d.items.length - 1} aria-label={`${coverLineText(it)}を${M.moveDown}`} onClick={() => bundle!.onMove(i, 1)} data-testid="cover-bundle-down">
                          {M.moveDown}
                        </button>
                        {bundle!.fixed?.has(it.key) ? null : (
                          <button type="button" className="btn text sm" aria-label={`${coverLineText(it)}を${M.removeItem}`} onClick={() => bundle!.onRemove(i)} data-testid="cover-bundle-remove">
                            {M.removeItem}
                          </button>
                        )}
                      </span>
                    </li>,
                    ...extra.map((x) => (
                      <li key={`${it.key}-${x.no}`} className="is-sub" data-testid="cover-auto-line">
                        <span className="no">{`${x.no}.`}</span>
                        <span className="nm">{x.l}</span>
                        <span className="cp">{`1${M.copiesUnit}`}</span>
                      </li>
                    )),
                  ])
                : auto.map((l, i) => (
                    <li key={`a${i}`} data-testid="cover-auto-line">
                      <span className="no">{lineNo(i)}</span>
                      <span className="nm">{coverLineText(l)}</span>
                      <span className="cp">{`${l.copies}${M.copiesUnit}`}</span>
                    </li>
                  ))}
              {f.manualLines.map((l, i) => (
                <li key={`m${i}`} className="is-manual" data-testid="cover-manual-line">
                  <span className="no">{lineNo(manualStart + i)}</span>
                  <input className="inp sm" aria-label={M.lineLabel} placeholder={M.lineLabel} value={l.label} maxLength={COVER_LETTER_LIMITS.lineLabel} onChange={(e) => setLine(i, { label: e.target.value })} data-testid="cover-line-label" />
                  <NumInput kind="int" className="inp sm num" aria-label={M.copies} value={l.copies} onChange={(v) => setLine(i, { copies: Math.min(COVER_LETTER_LIMITS.copies, Math.max(1, v ?? 1)) })} data-testid="cover-line-copies" />
                  <span className="u">{M.copiesUnit}</span>
                  {fax ? <NumInput kind="int" className="inp sm num" aria-label={M.pages} placeholder={M.pages} value={l.pages} onChange={(v) => setLine(i, { pages: v == null ? null : Math.min(COVER_LETTER_LIMITS.pages, Math.max(1, v)) })} data-testid="cover-line-pages" /> : null}
                  <button type="button" className="btn text sm" aria-label={`${l.label || M.lineLabel}の${M.removeLine}`} onClick={() => c.update({ manualLines: f.manualLines.filter((_, j) => j !== i) })} data-testid="cover-line-remove">
                    削除
                  </button>
                </li>
              ))}
            </ol>
            <div className="cladd">
              {onAddDocs ? (
                <button type="button" className="btn tonal sm" onClick={onAddDocs} data-testid="cover-add-docs">
                  {addDocsLabel}
                </button>
              ) : null}
              <button type="button" className="btn text" disabled={manualMax} onClick={() => c.update({ manualLines: [...f.manualLines, { label: "", copies: 1, pages: null }] })} data-testid="cover-line-add">
                {M.addLine}
              </button>
            </div>
            {c.bundleError ? (
              <p className="err" role="alert" data-testid="cover-bundle-error">
                {`⚠ ${c.bundleError}`}
              </p>
            ) : null}
          </div>
          {c.previewErr ? <ErrorText>{c.previewErr}</ErrorText> : null}
        </div>
      ) : null}
    </aside>
  );
}

/** {order}の約款の別紙（#233 2c）：スイッチ「約款」（付けないときの注意の1行は出さない＝docs/93 §16.4 ヌ。説明は「？」） */
function TermsRows({ x }: { x: CoverTermsControls }) {
  return (
    <div className="clterms" data-testid="cover-terms">
      {x.rows.map((r) => (
        <div className="clrow clwin" key={r.id}>
          <Switch checked={r.on} onChange={(v) => x.onChange(r.id, v)} label={r.label} testId="cover-terms-switch">
            <span>{r.label}</span>
          </Switch>
          <Help label={`${ORDER_MSG.termsPrint}の説明`} text={t(ORDER_MSG.termsPrintHelp)} testId="help-cover-terms" />
        </div>
      ))}
    </div>
  );
}

/** 未登録の送付先の欄（★13）：会社名（必須）・部署・担当者・郵便番号・住所と「{customer}に登録」。FAX は「送信先 FAX」の欄 */
function UnregisteredFields({ ids, f, update }: { ids: string; f: CoverForm; update: (p: Partial<CoverForm>) => void }) {
  const r = f.recipient;
  const set = (p: Partial<CoverRecipientInput>) => update({ recipient: { ...r, ...p } });
  return (
    <div className="clunreg" role="group" aria-label={`${M.to}（${M.kindUnregistered}）`} data-testid="cover-unregistered">
      <div className="clto">
        <span className="cllbl">{M.to}</span>
        <span className="chip dlv-un">{M.kindUnregistered}</span>
        <Help label={`${M.kindUnregistered}の説明`} text={M.recipientHelp} testId="help-cover-unregistered" />
      </div>
      <Field id={`${ids}un`} label={M.recipientName}>
        <input id={`${ids}un`} className="inp" placeholder=" " value={r.name} maxLength={COVER_LETTER_LIMITS.recipientName} onChange={(e) => set({ name: e.target.value })} data-testid="cover-un-name" />
      </Field>
      {!r.name.trim() ? <p className="hint" data-testid="cover-un-name-missing">{M.recipientNameRequired}</p> : null}
      <Field id={`${ids}ud`} label={M.recipientDepartment}>
        <input id={`${ids}ud`} className="inp" placeholder=" " value={r.department} maxLength={COVER_LETTER_LIMITS.recipientDepartment} onChange={(e) => set({ department: e.target.value })} data-testid="cover-un-dept" />
      </Field>
      <Field id={`${ids}uc`} label={M.recipientContact}>
        <input id={`${ids}uc`} className="inp" placeholder=" " value={r.contact} maxLength={COVER_LETTER_LIMITS.recipientContact} onChange={(e) => set({ contact: e.target.value })} data-testid="cover-un-contact" />
      </Field>
      <Field id={`${ids}uz`} label={M.recipientPostal}>
        <input id={`${ids}uz`} className="inp" inputMode="numeric" autoComplete="off" placeholder=" " value={r.postalCode} maxLength={10} onChange={(e) => set({ postalCode: e.target.value })} data-testid="cover-un-zip" />
      </Field>
      <Field id={`${ids}ua`} label={M.recipientAddress}>
        <textarea id={`${ids}ua`} className="inp" rows={2} placeholder=" " value={r.address} maxLength={COVER_LETTER_LIMITS.recipientAddress} onChange={(e) => set({ address: e.target.value })} data-testid="cover-un-address" />
      </Field>
    </div>
  );
}

/** 未登録の送付先を{customer}に登録する（既定オフ。★13）。FAX の欄の下に置く */
function RegisterPartnerCheck({ f, update }: { f: CoverForm; update: (p: Partial<CoverForm>) => void }) {
  return (
    <label className="clcheck">
      <input type="checkbox" checked={f.registerPartner} onChange={(e) => update({ registerPartner: e.target.checked })} data-testid="cover-un-register" />
      <span>{t(M.registerPartner)}</span>
      <Help label={t(M.registerPartner)} text={t(M.registerPartnerHelp)} testId="help-cover-un-register" />
    </label>
  );
}
